/**
 * Button Component
 * 
 * A reusable, accessible button component with multiple variants and sizes.
 * Follows WCAG 2.1 AA accessibility guidelines.
 * 
 * Features:
 * - Multiple variants (primary, secondary, danger)
 * - Multiple sizes (small, medium, large)
 * - Disabled state
 * - Full keyboard accessibility
 * - Proper ARIA attributes
 */

import React from 'react';
import styles from './Button.module.scss';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'secondary' | 'danger';
  /** Size of the button */
  size?: 'small' | 'medium' | 'large';
  /** Button content */
  children: React.ReactNode;
  /** Additional CSS class */
  className?: string;
}

/**
 * Button component
 * 
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleClick}>
 *   Click Me
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  className = '',
  disabled = false,
  type = 'button',
  ...props
}) => {
  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[size],
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};